import React, { useMemo, useState, useCallback } from 'react';
import {
  Calendar,
  CreditCard,
  CheckCircle,
  ArrowUpDown,
  FileText,
  HandCoins,
  Banknote,
  AlertTriangle,
  Users,
  Wallet,
} from 'lucide-react';
import {
  useFinanceRecords,
  useHandLoanRecords,
  useUnifiedCustomers,
  useAdminEmiPayments,
  type UnifiedLoanFacility,
} from '../../../lib/workspaceStore';
import DateRangeFilter from '../../common/DateRangeFilter';
import { useDateRange, filterByRange, formatRangeLabel } from '../../../lib/dateFilter';
import { ExportMenu, type ExportRow, type ReportBlock } from '../../../lib/exportReports';
import { readServiceLineNames } from '../../../lib/financeCatalogs';
import { resolveWorkspaceAsOfMs } from '../../../lib/workspaceAsOf';
import { computeFinancePeriodMetrics } from '../../../lib/portfolioMetrics';
import { StatsCard } from '../components/StatsCard';
import { ConsolidatedReport } from '../components/ConsolidatedReport';
import {
  buildFinanceCreatedRows,
  buildHandCreatedRows,
  buildFinanceClosedRows,
  buildHandClosedRows,
  buildFinanceSeizedRows,
  fmtInr,
  fmtInrCompact,
} from '../reportMetrics';
import { useTranslation } from '../../../lib/i18n';

function fmtDate(s?: string) {
  if (!s || s === '—') return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

type LoanKindFilter = 'all' | 'finance' | 'handloan';
type ReportSort = 'line_asc' | 'line_desc' | 'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc' | 'name_asc';

function facilityLine(f: UnifiedLoanFacility, lineByRecord: Map<string, string>): string {
  if (f.kind !== 'finance') return '—';
  return lineByRecord.get(f.recordId) || '—';
}

function sortRows<T>(rows: T[], sort: ReportSort, getters: { line: (r: T) => string; date: (r: T) => string; amount: (r: T) => number; name: (r: T) => string }): T[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    switch (sort) {
      case 'line_asc':
        return getters.line(a).localeCompare(getters.line(b), 'en', { sensitivity: 'base' });
      case 'line_desc':
        return getters.line(b).localeCompare(getters.line(a), 'en', { sensitivity: 'base' });
      case 'date_asc':
        return getters.date(a).localeCompare(getters.date(b));
      case 'date_desc':
        return getters.date(b).localeCompare(getters.date(a));
      case 'amount_asc':
        return getters.amount(a) - getters.amount(b);
      case 'amount_desc':
        return getters.amount(b) - getters.amount(a);
      case 'name_asc':
      default:
        return getters.name(a).localeCompare(getters.name(b), 'en', { sensitivity: 'base' });
    }
  });
  return copy;
}

function ReportSection({
  title,
  subtitle,
  exportTitle,
  exportFilename,
  rows,
  children,
  minTableWidth = 'min-w-[36rem]',
}: {
  title: string;
  subtitle: string;
  exportTitle: string;
  exportFilename: string;
  rows: ExportRow[];
  children: React.ReactNode;
  minTableWidth?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="app-card min-w-0 overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-zinc-200 bg-zinc-50/80 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-zinc-900">{title}</h2>
          <p className="text-xs text-zinc-500">{subtitle}</p>
          <p className="mt-1 text-[11px] text-zinc-400 lg:hidden">{t('common.swipeTable')}</p>
        </div>
        <div className="w-full shrink-0 sm:w-auto">
          <ExportMenu title={exportTitle} excelFilename={exportFilename} rows={rows} />
        </div>
      </div>
      <div className="app-table-wrap -mx-px overflow-x-auto overscroll-x-contain rounded-none border-0 border-t border-zinc-200">
        <div className={minTableWidth}>{children}</div>
      </div>
    </div>
  );
}

export function DayReport({ setActiveItem: _setActiveItem }: { setActiveItem: (item: string) => void }) {
  const { t } = useTranslation();
  const customers = useUnifiedCustomers();
  const payments = useAdminEmiPayments();
  const finances = useFinanceRecords();
  const handLoans = useHandLoanRecords();
  const dr = useDateRange('this_month');
  const [loanType, setLoanType] = useState<LoanKindFilter>('all');
  const [lineFilter, setLineFilter] = useState<string>('all');
  const [reportSort, setReportSort] = useState<ReportSort>('date_desc');
  const [consolidatedRows, setConsolidatedRows] = useState<ExportRow[]>([]);
  const onConsolidatedRows = useCallback((rows: ExportRow[]) => {
    setConsolidatedRows((prev) => (JSON.stringify(prev) === JSON.stringify(rows) ? prev : rows));
  }, []);
  const serviceLines = useMemo(() => readServiceLineNames(), []);
  const lineByRecord = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of finances) {
      const line = String(r.data.line ?? '').trim();
      if (line) m.set(r.id, line);
    }
    return m;
  }, [finances]);

  const loanTypeLabel = loanType === 'finance' ? t('common.finance') : loanType === 'handloan' ? t('common.handLoans') : t('portal.allLoans');
  const lineLabel = lineFilter === 'all' ? `${t('common.all')} ${t('common.line')}` : lineFilter;
  const rangeLabel = formatRangeLabel(dr.range);
  const showFinance = loanType !== 'handloan';
  const showHand = loanType !== 'finance';

  const matchesLine = useCallback(
    (f: UnifiedLoanFacility) => {
      if (lineFilter === 'all') return true;
      if (f.kind !== 'finance') return false;
      return (lineByRecord.get(f.recordId) || '') === lineFilter;
    },
    [lineFilter, lineByRecord],
  );

  const paymentsInRange = useMemo(
    () =>
      filterByRange(payments, dr.range, (p) => p.paidAt).filter((p) => {
        if (loanType !== 'all' && p.kind !== loanType) return false;
        if (lineFilter === 'all') return true;
        if (p.kind !== 'finance') return false;
        return (lineByRecord.get(p.recordId || '') || '') === lineFilter;
      }),
    [payments, dr.range, loanType, lineFilter, lineByRecord],
  );

  const financeCreatedRows = useMemo(() => {
    const rows = buildFinanceCreatedRows(finances, dr.range, lineFilter);
    return sortRows(rows, reportSort, {
      line: (r) => r.line,
      date: (r) => r.createdAt,
      amount: (r) => r.principal,
      name: (r) => r.customer,
    });
  }, [finances, dr.range, lineFilter, reportSort]);

  const handCreatedRows = useMemo(() => {
    const rows = buildHandCreatedRows(handLoans, dr.range);
    return sortRows(rows, reportSort, {
      line: () => '—',
      date: (r) => r.createdAt,
      amount: (r) => r.principal,
      name: (r) => r.borrower,
    });
  }, [handLoans, dr.range, reportSort]);

  const financeClosedRows = useMemo(() => {
    const rows = buildFinanceClosedRows(finances, payments, dr.range, lineFilter);
    return sortRows(rows, reportSort, {
      line: (r) => r.line,
      date: (r) => r.closedAt,
      amount: (r) => r.principal,
      name: (r) => r.customer,
    });
  }, [finances, payments, dr.range, lineFilter, reportSort]);

  const handClosedRows = useMemo(() => {
    const rows = buildHandClosedRows(handLoans, payments, dr.range);
    return sortRows(rows, reportSort, {
      line: () => '—',
      date: (r) => r.closedAt,
      amount: (r) => r.principal,
      name: (r) => r.borrower,
    });
  }, [handLoans, payments, dr.range, reportSort]);

  const financeSeizedRows = useMemo(() => {
    const rows = buildFinanceSeizedRows(finances, dr.range, lineFilter);
    return sortRows(rows, reportSort, {
      line: (r) => r.line,
      date: (r) => r.seizedAt,
      amount: (r) => r.principal,
      name: (r) => r.customer,
    });
  }, [finances, dr.range, lineFilter, reportSort]);

  const periodMetrics = useMemo(() => {
    const financeCreatedAmount = financeCreatedRows.reduce((s, r) => s + r.principal, 0);
    const handCreatedAmount = handCreatedRows.reduce((s, r) => s + r.principal, 0);
    const financeClosedAmount = financeClosedRows.reduce((s, r) => s + r.principal, 0);
    const handClosedAmount = handClosedRows.reduce((s, r) => s + r.principal, 0);
    const financeSeizedAmount = financeSeizedRows.reduce((s, r) => s + r.principal, 0);
    const financeMetrics = computeFinancePeriodMetrics(paymentsInRange, { start: null, end: null });
    const collectionsAmount = financeMetrics.emiCollected;
    const lateFeesAmount = financeMetrics.lateFeesCollected;
    const totalCollections = financeMetrics.totalCollections;
    return {
      financeCreatedCount: financeCreatedRows.length,
      financeCreatedAmount,
      handCreatedCount: handCreatedRows.length,
      handCreatedAmount,
      financeClosedCount: financeClosedRows.length,
      financeClosedAmount,
      handClosedCount: handClosedRows.length,
      handClosedAmount,
      financeSeizedCount: financeSeizedRows.length,
      financeSeizedAmount,
      collectionsCount: paymentsInRange.length,
      collectionsAmount,
      lateFeesAmount,
      totalCollections,
    };
  }, [financeCreatedRows, handCreatedRows, financeClosedRows, handClosedRows, financeSeizedRows, paymentsInRange]);

  const summaryExport: ExportRow[] = useMemo(() => {
    const rows: ExportRow[] = [];
    if (showFinance) {
      rows.push(
        { Metric: 'Finance created', Count: periodMetrics.financeCreatedCount, Amount: periodMetrics.financeCreatedAmount },
        { Metric: 'Finance closed', Count: periodMetrics.financeClosedCount, Amount: periodMetrics.financeClosedAmount },
        { Metric: 'Finance seized', Count: periodMetrics.financeSeizedCount, Amount: periodMetrics.financeSeizedAmount },
      );
    }
    if (showHand) {
      rows.push(
        { Metric: 'Hand loan created', Count: periodMetrics.handCreatedCount, Amount: periodMetrics.handCreatedAmount },
        { Metric: 'Hand loan closed', Count: periodMetrics.handClosedCount, Amount: periodMetrics.handClosedAmount },
      );
    }
    rows.push(
      { Metric: 'Collections', Count: periodMetrics.collectionsCount, Amount: periodMetrics.collectionsAmount },
      { Metric: 'Late fees', Count: '—', Amount: periodMetrics.lateFeesAmount },
    );
    return rows;
  }, [periodMetrics, showFinance, showHand]);

  const financeCreatedExport: ExportRow[] = financeCreatedRows.map((r) => ({
    Created: fmtDate(r.createdAt),
    Customer: r.customer,
    RegHA: r.regHa,
    Line: r.line,
    Principal: r.principal,
  }));

  const handCreatedExport: ExportRow[] = handCreatedRows.map((r) => ({
    Created: fmtDate(r.createdAt),
    Borrower: r.borrower,
    LoanType: r.loanType,
    Repayment: r.repayment,
    Principal: r.principal,
  }));

  const financeClosedExport: ExportRow[] = financeClosedRows.map((r) => ({
    Closed: fmtDate(r.closedAt),
    Customer: r.customer,
    RegHA: r.regHa,
    Line: r.line,
    Principal: r.principal,
  }));

  const handClosedExport: ExportRow[] = handClosedRows.map((r) => ({
    Closed: fmtDate(r.closedAt),
    Borrower: r.borrower,
    LoanType: r.loanType,
    Principal: r.principal,
  }));

  const financeSeizedExport: ExportRow[] = financeSeizedRows.map((r) => ({
    Seized: fmtDate(r.seizedAt),
    Customer: r.customer,
    RegHA: r.regHa,
    Line: r.line,
    Reason: r.reason,
    Principal: r.principal,
  }));

  const collectionsExport: ExportRow[] = paymentsInRange.map((p) => ({
    Date: fmtDate(p.paidAt),
    Line: p.kind === 'finance' ? lineByRecord.get(p.recordId || '') || '—' : '—',
    Facility: p.facilityTitle || '—',
    Type: p.kind === 'finance' ? 'Finance' : 'Hand loan',
    Amount: p.amount,
    LateFee: p.lateFeeAmount || 0,
    CreditedMonths: p.creditedMonths ?? 0,
  }));

  const outstandingRows = useMemo(() => {
    const rows = customers
      .filter((c) => c.pendingLoan > 0)
      .map((c) => {
        const financeFacilities = c.facilities.filter((f) => f.kind === 'finance' && matchesLine(f));
        const hasHand = c.facilities.some((f) => f.kind === 'handloan');
        if (loanType === 'finance' && !financeFacilities.length) return null;
        if (loanType === 'handloan' && !hasHand) return null;
        const line = financeFacilities.length > 0 ? facilityLine(financeFacilities[0], lineByRecord) : '—';
        if (lineFilter !== 'all' && !financeFacilities.length) return null;
        const due = c.pendingEmi?.dueDate && c.pendingEmi.dueDate !== '—' ? c.pendingEmi.dueDate : '';
        const overdue = due
          ? new Date(`${due}T23:59:59`).getTime() < resolveWorkspaceAsOfMs({ allowWallClockFallback: true })
          : false;
        return {
          name: c.name,
          line,
          phone: c.phone || '—',
          principal: Math.round(c.totalLoan),
          collected: Math.round(c.paidLoan),
          outstanding: Math.round(c.pendingLoan),
          nextDue: due || '—',
          status: overdue ? 'Overdue' : 'Current',
        };
      })
      .filter((r): r is NonNullable<typeof r> => r != null);
    return sortRows(rows, reportSort, {
      line: (r) => r.line,
      date: (r) => r.nextDue,
      amount: (r) => r.outstanding,
      name: (r) => r.name,
    });
  }, [customers, lineFilter, lineByRecord, reportSort, matchesLine, loanType]);

  const outstandingExport: ExportRow[] = outstandingRows.map((r) => ({
    Line: r.line,
    Borrower: r.name,
    Phone: r.phone,
    Principal: r.principal,
    Collected: r.collected,
    Outstanding: r.outstanding,
    NextDue: r.nextDue,
    Status: r.status,
  }));

  const allReportSections: ReportBlock[] = useMemo(() => {
    const dated = (name: string) => `${name} · ${rangeLabel}`;
    const blocks: ReportBlock[] = [
      {
        title: dated('Period summary'),
        rows: [{ Metric: 'Date range', Count: rangeLabel, Amount: '' }, ...summaryExport],
      },
    ];
    if (showFinance) {
      blocks.push(
        { title: dated(t('reports.financeCreated')), rows: financeCreatedExport },
        { title: dated(t('reports.financeClosed')), rows: financeClosedExport },
        { title: dated(t('reports.financeSeized')), rows: financeSeizedExport },
      );
    }
    if (showHand) {
      blocks.push(
        { title: dated(t('reports.handCreated')), rows: handCreatedExport },
        { title: dated(t('reports.handClosed')), rows: handClosedExport },
      );
    }
    const lateFeeExport = collectionsExport.filter((row) => Number(row.LateFee) > 0);
    blocks.push(
      { title: dated(t('reports.collections')), rows: collectionsExport },
      { title: dated(t('reports.lateFees')), rows: lateFeeExport },
      { title: 'Outstanding and overdue (open now)', rows: outstandingExport },
      { title: 'Overdue only (open now)', rows: outstandingExport.filter((row) => row.Status === 'Overdue') },
    );
    if (consolidatedRows.length) {
      blocks.push({ title: dated('Consolidated summary'), rows: consolidatedRows });
    }
    return blocks;
  }, [
    rangeLabel,
    summaryExport,
    showFinance,
    showHand,
    t,
    financeCreatedExport,
    financeClosedExport,
    financeSeizedExport,
    handCreatedExport,
    handClosedExport,
    collectionsExport,
    outstandingExport,
    consolidatedRows,
  ]);

  const portfolioStats = useMemo(() => {
    const totalDue = outstandingRows.reduce((s, r) => s + r.outstanding, 0);
    const overdueCount = outstandingRows.filter((r) => r.status === 'Overdue').length;
    return { totalDue, overdueCount };
  }, [outstandingRows]);

  const kpiCards = useMemo(() => {
    const cards: {
      title: string;
      titleLong: string;
      value: string;
      subValue?: string;
      icon: typeof Calendar;
      color: string;
      accent: string;
    }[] = [];

    if (showFinance) {
      cards.push(
        {
          title: t('reports.financeNew'),
          titleLong: t('reports.financeCreated'),
          value: String(periodMetrics.financeCreatedCount),
          subValue: fmtInrCompact(periodMetrics.financeCreatedAmount),
          icon: FileText,
          color: 'text-sky-700',
          accent: 'border-l-sky-600',
        },
        {
          title: t('reports.financeClosedShort'),
          titleLong: t('reports.financeClosed'),
          value: String(periodMetrics.financeClosedCount),
          subValue: fmtInrCompact(periodMetrics.financeClosedAmount),
          icon: CheckCircle,
          color: 'text-violet-700',
          accent: 'border-l-violet-600',
        },
      );
      if (periodMetrics.financeSeizedCount > 0) {
        cards.push({
          title: t('reports.seized'),
          titleLong: t('reports.financeSeized'),
          value: String(periodMetrics.financeSeizedCount),
          subValue: fmtInrCompact(periodMetrics.financeSeizedAmount),
          icon: AlertTriangle,
          color: 'text-rose-700',
          accent: 'border-l-rose-600',
        });
      }
    }

    if (showHand) {
      cards.push(
        {
          title: t('reports.handNew'),
          titleLong: t('reports.handCreated'),
          value: String(periodMetrics.handCreatedCount),
          subValue: fmtInrCompact(periodMetrics.handCreatedAmount),
          icon: HandCoins,
          color: 'text-amber-700',
          accent: 'border-l-amber-500',
        },
        {
          title: t('reports.handClosedShort'),
          titleLong: t('reports.handClosed'),
          value: String(periodMetrics.handClosedCount),
          subValue: fmtInrCompact(periodMetrics.handClosedAmount),
          icon: CheckCircle,
          color: 'text-teal-700',
          accent: 'border-l-teal-600',
        },
      );
    }

    cards.push(
      {
        title: t('reports.collected'),
        titleLong: t('reports.collections'),
        value: fmtInrCompact(periodMetrics.collectionsAmount),
        subValue: `${periodMetrics.collectionsCount} payment(s)`,
        icon: CreditCard,
        color: 'text-emerald-700',
        accent: 'border-l-emerald-600',
      },
      {
        title: t('reports.lateFees'),
        titleLong: t('reports.lateFees'),
        value: fmtInrCompact(periodMetrics.lateFeesAmount),
        icon: Banknote,
        color: 'text-orange-700',
        accent: 'border-l-orange-500',
      },
      {
        title: t('reports.openHps'),
        titleLong: t('reports.outstandingOverdue'),
        value: String(outstandingRows.length),
        subValue:
          portfolioStats.overdueCount > 0
            ? `${portfolioStats.overdueCount} overdue`
            : outstandingRows.length > 0
              ? 'All current'
              : undefined,
        icon: Users,
        color: 'text-indigo-700',
        accent: 'border-l-indigo-600',
      },
      {
        title: t('reports.amtDue'),
        titleLong: t('common.outstanding'),
        value: fmtInrCompact(portfolioStats.totalDue),
        icon: Wallet,
        color: 'text-rose-700',
        accent: 'border-l-rose-600',
      },
    );

    return cards;
  }, [showFinance, showHand, periodMetrics, outstandingRows.length, portfolioStats, t]);

  return (
    <div className="app-page min-w-0 space-y-4 pb-2 sm:space-y-5">
      <header className="app-page-header flex-col items-stretch gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-800">{t('reports.title')}</p>
          <h1 className="app-page-title mt-0.5">{t('reports.title')}</h1>
          <p className="app-page-sub line-clamp-2 sm:line-clamp-none">
            {t('reports.subtitle')} · {loanTypeLabel} · {lineLabel} · {rangeLabel}
          </p>
        </div>
        <div className="w-full shrink-0 sm:w-auto">
          <p className="mb-1 text-[11px] font-medium text-zinc-500">All reports for {rangeLabel}</p>
          <ExportMenu
            title={`All reports (${rangeLabel})`}
            excelFilename="all-reports.xls"
            rows={summaryExport}
            sections={allReportSections}
            landscape
            className="w-full sm:ml-auto sm:inline-block sm:w-auto"
          />
        </div>
      </header>

      <DateRangeFilter
        start={dr.range.start}
        end={dr.range.end}
        preset={dr.preset}
        onStart={dr.setStart}
        onEnd={dr.setEnd}
        onPreset={dr.setPreset}
        onClear={dr.clear}
        className="!w-full"
      />

      <div className="app-card app-card-inner min-w-0">
        <div className="mb-3 flex items-center gap-2 text-zinc-700">
          <ArrowUpDown className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{t('common.filter')}</p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label htmlFor="report-loan-type" className="app-label">
              {t('reports.loanType')}
            </label>
            <select
              id="report-loan-type"
              value={loanType}
              onChange={(e) => setLoanType(e.target.value as LoanKindFilter)}
              className="app-field w-full"
            >
              <option value="all">{t('portal.allLoans')}</option>
              <option value="finance">{t('common.finance')}</option>
              <option value="handloan">{t('common.handLoans')}</option>
            </select>
          </div>
          <div>
            <label htmlFor="report-line" className="app-label">
              {t('reports.serviceLine')}
            </label>
            <select id="report-line" value={lineFilter} onChange={(e) => setLineFilter(e.target.value)} className="app-field w-full">
              <option value="all">{t('common.all')} {t('common.line')}</option>
              {serviceLines.map((line) => (
                <option key={line} value={line}>
                  {line}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="report-sort" className="app-label">
              {t('reports.sortBy')}
            </label>
            <select
              id="report-sort"
              value={reportSort}
              onChange={(e) => setReportSort(e.target.value as ReportSort)}
              className="app-field w-full"
            >
              <option value="line_asc">Line (A–Z)</option>
              <option value="line_desc">Line (Z–A)</option>
              <option value="date_desc">Date (newest)</option>
              <option value="date_asc">Date (oldest)</option>
              <option value="amount_desc">Amount (high–low)</option>
              <option value="amount_asc">Amount (low–high)</option>
              <option value="name_asc">Borrower (A–Z)</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">{t('common.total')} · {rangeLabel}</p>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4 lg:gap-3">
          {kpiCards.map((card, index) => (
            <StatsCard
              key={index}
              title={card.title}
              titleLong={card.titleLong}
              value={card.value}
              subValue={card.subValue}
              icon={card.icon}
              color={card.color}
              accent={card.accent}
            />
          ))}
        </div>
      </div>

      <ConsolidatedReport range={dr.range} rangeLabel={rangeLabel} onExportRows={onConsolidatedRows} />

      {showFinance && (
        <ReportSection
          title={t('reports.financeCreated')}
          subtitle={`${financeCreatedRows.length} ${t('common.inPeriod')} · ${rangeLabel}`}
          exportTitle={`${t('reports.financeCreated')} (${rangeLabel})`}
          exportFilename="finance-created.xls"
          rows={financeCreatedExport}
          minTableWidth="min-w-[40rem]"
        >
          <table className="app-table min-w-full">
            <thead>
              <tr>
                <th>{t('common.created')}</th>
                <th>{t('common.customer')}</th>
                <th>{t('reports.regHa')}</th>
                <th>{t('common.line')}</th>
                <th className="text-right">{t('common.principal')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 bg-white">
              {financeCreatedRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-sm text-zinc-500">
                    No finance accounts created in this period.
                  </td>
                </tr>
              ) : (
                financeCreatedRows.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap">{fmtDate(r.createdAt)}</td>
                    <td className="font-medium text-zinc-900">{r.customer}</td>
                    <td className="text-zinc-500">{r.regHa}</td>
                    <td className="text-zinc-500">{r.line}</td>
                    <td className="text-right font-semibold tabular-nums">{fmtInr(r.principal)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ReportSection>
      )}

      {showHand && (
        <ReportSection
          title={t('reports.handCreated')}
          subtitle={`${handCreatedRows.length} ${t('common.inPeriod')} · ${rangeLabel}`}
          exportTitle={`${t('reports.handCreated')} (${rangeLabel})`}
          exportFilename="hand-loans-created.xls"
          rows={handCreatedExport}
          minTableWidth="min-w-[40rem]"
        >
          <table className="app-table min-w-full">
            <thead>
              <tr>
                <th>{t('common.created')}</th>
                <th>{t('loans.borrower')}</th>
                <th>{t('loans.type')}</th>
                <th>{t('loans.term')}</th>
                <th className="text-right">{t('common.principal')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 bg-white">
              {handCreatedRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-sm text-zinc-500">
                    No hand loans created in this period.
                  </td>
                </tr>
              ) : (
                handCreatedRows.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap">{fmtDate(r.createdAt)}</td>
                    <td className="font-medium text-zinc-900">{r.borrower}</td>
                    <td className="text-zinc-500">{r.loanType}</td>
                    <td className="text-zinc-500">{r.repayment}</td>
                    <td className="text-right font-semibold tabular-nums">{fmtInr(r.principal)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ReportSection>
      )}

      {showFinance && (
        <ReportSection
          title={t('reports.financeClosed')}
          subtitle={`${financeClosedRows.length} ${t('common.inPeriod')}`}
          exportTitle={`${t('reports.financeClosed')} (${rangeLabel})`}
          exportFilename="finance-closed.xls"
          rows={financeClosedExport}
          minTableWidth="min-w-[40rem]"
        >
          <table className="app-table min-w-full">
            <thead>
              <tr>
                <th>{t('status.closed')}</th>
                <th>{t('common.customer')}</th>
                <th>{t('reports.regHa')}</th>
                <th>{t('common.line')}</th>
                <th className="text-right">{t('common.principal')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 bg-white">
              {financeClosedRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-sm text-zinc-500">
                    No finance accounts closed in this period.
                  </td>
                </tr>
              ) : (
                financeClosedRows.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap">{fmtDate(r.closedAt)}</td>
                    <td className="font-medium text-zinc-900">{r.customer}</td>
                    <td className="text-zinc-500">{r.regHa}</td>
                    <td className="text-zinc-500">{r.line}</td>
                    <td className="text-right font-semibold tabular-nums">{fmtInr(r.principal)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ReportSection>
      )}

      {showHand && (
        <ReportSection
          title={t('reports.handClosed')}
          subtitle={`${handClosedRows.length} ${t('common.inPeriod')}`}
          exportTitle={`${t('reports.handClosed')} (${rangeLabel})`}
          exportFilename="hand-loans-closed.xls"
          rows={handClosedExport}
          minTableWidth="min-w-[36rem]"
        >
          <table className="app-table min-w-full">
            <thead>
              <tr>
                <th>{t('status.closed')}</th>
                <th>{t('loans.borrower')}</th>
                <th>{t('loans.type')}</th>
                <th className="text-right">{t('common.principal')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 bg-white">
              {handClosedRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-sm text-zinc-500">
                    No hand loans closed in this period.
                  </td>
                </tr>
              ) : (
                handClosedRows.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap">{fmtDate(r.closedAt)}</td>
                    <td className="font-medium text-zinc-900">{r.borrower}</td>
                    <td className="text-zinc-500">{r.loanType}</td>
                    <td className="text-right font-semibold tabular-nums">{fmtInr(r.principal)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ReportSection>
      )}

      {showFinance && financeSeizedRows.length > 0 && (
        <ReportSection
          title={t('reports.financeSeized')}
          subtitle={`${financeSeizedRows.length} · ${rangeLabel}`}
          exportTitle={`${t('reports.financeSeized')} (${rangeLabel})`}
          exportFilename="finance-seized.xls"
          rows={financeSeizedExport}
          minTableWidth="min-w-[44rem]"
        >
          <table className="app-table min-w-full">
            <thead>
              <tr>
                <th>{t('reports.seized')}</th>
                <th>{t('common.customer')}</th>
                <th>{t('reports.regHa')}</th>
                <th>{t('common.line')}</th>
                <th>{t('customer.note')}</th>
                <th className="text-right">{t('common.principal')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 bg-white">
              {financeSeizedRows.map((r) => (
                <tr key={r.id}>
                  <td className="whitespace-nowrap">{fmtDate(r.seizedAt)}</td>
                  <td className="font-medium text-zinc-900">{r.customer}</td>
                  <td className="text-zinc-500">{r.regHa}</td>
                  <td className="text-zinc-500">{r.line}</td>
                  <td className="max-w-[12rem] truncate text-zinc-600">{r.reason}</td>
                  <td className="text-right font-semibold tabular-nums">{fmtInr(r.principal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportSection>
      )}

      <ReportSection
        title={t('reports.collections')}
        subtitle={`${paymentsInRange.length} ${t('common.records')} · ${rangeLabel}`}
        exportTitle={`${t('reports.collections')} (${rangeLabel})`}
        exportFilename="collections.xls"
        rows={collectionsExport}
        minTableWidth="min-w-[40rem]"
      >
        <table className="app-table min-w-full">
          <thead>
            <tr>
              <th>{t('common.date')}</th>
              <th>{t('common.line')}</th>
              <th>{t('customer.facility')}</th>
              <th>{t('customer.kind')}</th>
              <th className="text-right">{t('common.amount')}</th>
              <th className="text-right">{t('reports.lateFee')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 bg-white">
            {paymentsInRange.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-sm text-zinc-500">
                  No collections in this period.
                </td>
              </tr>
            ) : (
              paymentsInRange.map((p) => (
                <tr key={p.id}>
                  <td className="whitespace-nowrap">{fmtDate(p.paidAt)}</td>
                  <td className="text-zinc-500">{p.kind === 'finance' ? lineByRecord.get(p.recordId || '') || '—' : '—'}</td>
                  <td className="max-w-[10rem] truncate text-zinc-700">{p.facilityTitle || '—'}</td>
                  <td className="text-zinc-500">{p.kind === 'finance' ? t('common.finance') : t('common.handLoans')}</td>
                  <td className="text-right font-semibold tabular-nums text-zinc-900">₹{(p.amount || 0).toLocaleString('en-IN')}</td>
                  <td className="text-right tabular-nums text-amber-700">{p.lateFeeAmount ? `₹${p.lateFeeAmount.toLocaleString('en-IN')}` : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </ReportSection>

      <ReportSection
        title={t('reports.outstandingOverdue')}
        subtitle={`${outstandingRows.length} ${t('common.borrower')}`}
        exportTitle={t('reports.outstandingOverdue')}
        exportFilename="outstanding.xls"
        rows={outstandingExport}
        minTableWidth="min-w-[52rem]"
      >
        <table className="app-table min-w-full">
          <thead>
            <tr>
              <th>{t('common.line')}</th>
              <th>{t('common.borrower')}</th>
              <th>{t('common.phone')}</th>
              <th className="text-right">{t('common.principal')}</th>
              <th className="text-right">{t('common.collected')}</th>
              <th className="text-right">{t('common.outstanding')}</th>
              <th>{t('common.nextDue')}</th>
              <th>{t('common.status')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 bg-white">
            {outstandingRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-8 text-center text-sm text-zinc-500">
                  No open balances.
                </td>
              </tr>
            ) : (
              outstandingRows.map((r, i) => (
                <tr key={`${r.name}-${i}`}>
                  <td className="text-zinc-500">{r.line}</td>
                  <td className="font-medium text-zinc-900">{r.name}</td>
                  <td className="text-zinc-500">{r.phone}</td>
                  <td className="text-right tabular-nums">₹{r.principal.toLocaleString('en-IN')}</td>
                  <td className="text-right tabular-nums text-emerald-700">₹{r.collected.toLocaleString('en-IN')}</td>
                  <td className="text-right font-semibold tabular-nums text-zinc-900">₹{r.outstanding.toLocaleString('en-IN')}</td>
                  <td className="whitespace-nowrap">{r.nextDue}</td>
                  <td>
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                        r.status === 'Overdue' ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'
                      }`}
                    >
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </ReportSection>
    </div>
  );
}
